import { Container } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import styles from "./cookie-policy.module.scss";

const CookiePolicy = () => {
  return (
    <div className={styles.cookiePage}>
      <Container>
        <div className={styles.header}>
          <Link to="/" className={styles.backLink}>
            ← Back to Home
          </Link>
          <h1 className={styles.title}>Cookie Policy</h1>
          <p className={styles.lastUpdated}>Last Updated: November 3, 2025</p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2>1. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your device (computer, smartphone, or tablet)
              when you visit a website. They are widely used to make websites work more efficiently and
              provide information to website owners.
            </p>
            <p>
              Cookies help websites remember your actions and preferences (such as login details, language,
              and other display preferences) over a period of time, so you don't have to keep re-entering
              them whenever you return to the site or browse from one page to another.
            </p>
          </section>

          <section className={styles.section}>
            <h2>2. How We Use Cookies</h2>
            <p>
              BioLean uses cookies to enhance your browsing experience and to understand how visitors
              interact with our website. We only use cookies after you have given us your explicit consent
              through our cookie consent banner.
            </p>
            <p>
              <strong>We do not use cookies for:</strong>
            </p>
            <ul>
              <li>Tracking you across other websites</li>
              <li>Selling your data to third parties</li>
              <li>Targeted advertising</li>
              <li>Identifying you personally</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>3. Types of Cookies We Use</h2>

            <h3>3.1 Strictly Necessary Cookies</h3>
            <div className={styles.cookieItem}>
              <p><strong>Cookie Name:</strong> <code>biolean_cookie_consent</code></p>
              <p><strong>Purpose:</strong> Stores your cookie consent preference (whether you accepted or declined cookies)</p>
              <p><strong>Duration:</strong> 365 days</p>
              <p><strong>Type:</strong> First-party cookie</p>
              <p><strong>Consent Required:</strong> No - this cookie is necessary for the cookie consent mechanism to function</p>
            </div>

            <h3>3.2 Analytics Cookies (Optional - Require Consent)</h3>
            <p>
              These cookies are only set if you click "Accept" on our cookie consent banner. They help us
              understand how visitors use our website so we can improve the user experience.
            </p>

            <div className={styles.cookieItem}>
              <p><strong>Cookie Name:</strong> <code>_ga</code></p>
              <p><strong>Purpose:</strong> Distinguishes unique users and calculates visitor, session, and campaign data for analytics reports</p>
              <p><strong>Duration:</strong> 2 years</p>
              <p><strong>Type:</strong> Third-party cookie (Google Analytics)</p>
              <p><strong>Provider:</strong> Google LLC</p>
            </div>

            <div className={styles.cookieItem}>
              <p><strong>Cookie Name:</strong> <code>_ga_[container-id]</code></p>
              <p><strong>Purpose:</strong> Maintains session state and tracks page views for Google Analytics 4</p>
              <p><strong>Duration:</strong> 2 years</p>
              <p><strong>Type:</strong> Third-party cookie (Google Analytics)</p>
              <p><strong>Provider:</strong> Google LLC</p>
            </div>

            <div className={styles.cookieItem}>
              <p><strong>Cookie Name:</strong> <code>_gid</code></p>
              <p><strong>Purpose:</strong> Stores information about how visitors use the website and helps create analytics reports</p>
              <p><strong>Duration:</strong> 24 hours</p>
              <p><strong>Type:</strong> Third-party cookie (Google Analytics)</p>
              <p><strong>Provider:</strong> Google LLC</p>
            </div>
          </section>

          <section className={styles.section}>
            <h2>4. Cookie Summary Table</h2>
            <div className={styles.cookieTable}>
              <table>
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>biolean_cookie_consent</td>
                    <td>Stores consent preference</td>
                    <td>365 days</td>
                    <td>Strictly Necessary</td>
                  </tr>
                  <tr>
                    <td>_ga</td>
                    <td>User identification</td>
                    <td>2 years</td>
                    <td>Analytics (Optional)</td>
                  </tr>
                  <tr>
                    <td>_ga_*</td>
                    <td>Session state</td>
                    <td>2 years</td>
                    <td>Analytics (Optional)</td>
                  </tr>
                  <tr>
                    <td>_gid</td>
                    <td>User identification</td>
                    <td>24 hours</td>
                    <td>Analytics (Optional)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.section}>
            <h2>5. Third-Party Cookies</h2>
            <p>
              When you consent to analytics cookies, we use Google Analytics, a web analytics service
              provided by Google LLC ("Google"). Google Analytics uses cookies to help us analyze how
              users interact with our website.
            </p>
            <p>
              The information generated by Google Analytics cookies about your use of our website
              (including your IP address, which is anonymized) will be transmitted to and stored by
              Google on servers primarily located in the United States.
            </p>
            <p>
              Google will use this information to evaluate your use of our website, compile reports
              on website activity, and provide other services relating to website activity and internet usage.
            </p>
            <p>
              <strong>Google's Privacy Policy:</strong>{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                https://policies.google.com/privacy
              </a>
            </p>
            <p>
              <strong>How Google uses data:</strong>{" "}
              <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
                https://policies.google.com/technologies/partner-sites
              </a>
            </p>
          </section>

          <section className={styles.section}>
            <h2>6. Managing Your Cookie Preferences</h2>
            <p>
              You have several options to control and manage cookies on our website:
            </p>

            <h3>6.1 Cookie Consent Banner</h3>
            <p>
              When you first visit our website, a cookie consent banner appears at the bottom of the page.
              You can choose to:
            </p>
            <ul>
              <li><strong>Accept:</strong> Allow all cookies (analytics cookies will be set)</li>
              <li><strong>Decline:</strong> Only strictly necessary cookies will be used</li>
            </ul>
            <p>
              Your choice is stored for 365 days. To change your preference, clear your browser cookies
              and revisit our website.
            </p>

            <h3>6.2 Browser Settings</h3>
            <p>
              Most web browsers allow you to control cookies through their settings. You can typically:
            </p>
            <ul>
              <li>See what cookies are stored and delete them individually</li>
              <li>Block third-party cookies</li>
              <li>Block all cookies from specific websites</li>
              <li>Block all cookies</li>
              <li>Delete all cookies when you close your browser</li>
            </ul>
            <p>
              <strong>Browser-specific instructions:</strong>
            </p>
            <ul>
              <li>
                <strong>Google Chrome:</strong>{" "}
                <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">
                  Manage cookies in Chrome
                </a>
              </li>
              <li>
                <strong>Mozilla Firefox:</strong>{" "}
                <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer">
                  Manage cookies in Firefox
                </a>
              </li>
              <li>
                <strong>Safari:</strong>{" "}
                <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">
                  Manage cookies in Safari
                </a>
              </li>
              <li>
                <strong>Microsoft Edge:</strong>{" "}
                <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">
                  Manage cookies in Edge
                </a>
              </li>
            </ul>

            <h3>6.3 Google Analytics Opt-Out</h3>
            <p>
              You can prevent Google Analytics from collecting your data by installing the
              Google Analytics Opt-out Browser Add-on:
            </p>
            <p>
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                Download Google Analytics Opt-out Add-on
              </a>
            </p>

            <h3>6.4 Do Not Track (DNT)</h3>
            <p>
              Some browsers have a "Do Not Track" (DNT) feature that lets you tell websites you do not
              want to have your online activities tracked. Our website respects the DNT signal - if DNT
              is enabled in your browser, we will not load analytics cookies even if you accept cookies
              in our banner.
            </p>
          </section>

          <section className={styles.section}>
            <h2>7. Impact of Disabling Cookies</h2>
            <p>
              If you choose to decline or delete cookies:
            </p>
            <ul>
              <li>
                <strong>Website Functionality:</strong> All core features of our website will continue
                to work normally. Our website does not rely on cookies for basic functionality.
              </li>
              <li>
                <strong>Analytics:</strong> We won't be able to track how visitors use our website,
                which helps us improve the user experience. However, this does not affect your ability
                to use the website.
              </li>
              <li>
                <strong>Cookie Banner:</strong> The cookie consent banner may reappear if you delete
                the consent cookie.
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>8. Data Protection and Privacy</h2>
            <p>
              Cookie data is processed in accordance with our Privacy Policy and applicable data
              protection laws, including the EU General Data Protection Regulation (GDPR) and the
              California Consumer Privacy Act (CCPA).
            </p>
            <p>
              For more information about how we collect, use, and protect your personal data, please
              see our{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
          </section>

          <section className={styles.section}>
            <h2>9. Updates to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices
              or for legal, operational, or regulatory reasons. The "Last Updated" date at the top of
              this page indicates when this policy was last revised.
            </p>
            <p>
              We encourage you to review this Cookie Policy periodically. If we make significant changes,
              we may notify you through a notice on our website.
            </p>
          </section>

          <section className={styles.section}>
            <h2>10. Contact Us</h2>
            <p>
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <ul className={styles.contactInfo}>
              <li>
                <strong>Email:</strong>{" "}
                <a href="mailto:info@biolean.dk">info@biolean.dk</a>
              </li>
              <li>
                <strong>Website:</strong>{" "}
                <a href="https://biolean.dk" target="_blank" rel="noopener noreferrer">
                  biolean.dk
                </a>
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>11. Legal Compliance</h2>
            <p>
              This Cookie Policy complies with:
            </p>
            <ul>
              <li>
                <strong>EU ePrivacy Directive (Cookie Law):</strong> Requires informed consent before
                storing or accessing information on a user's device
              </li>
              <li>
                <strong>GDPR (General Data Protection Regulation):</strong> EU regulation on data
                protection and privacy
              </li>
              <li>
                <strong>CCPA (California Consumer Privacy Act):</strong> California law giving consumers
                more control over their personal information
              </li>
            </ul>
            <p>
              We obtain your explicit consent before setting any non-essential cookies, and you have
              the right to withdraw your consent at any time by managing your cookie preferences.
            </p>
          </section>
        </div>

        <div className={styles.footer}>
          <Link to="/" className={styles.backLink}>
            ← Back to Home
          </Link>
        </div>
      </Container>
    </div>
  );
};

export default CookiePolicy;
